<template>
  <section class="all-cases-view fax-in-process-view">
    <div class="navbarComponent">
      <div class="container-default">
        <div class="listings-navbar">
          <el-row :gutter="20" style="row-gap: 5px">
            <el-col :xl="14">
              <ul class="d-flex list-items-group-15">
                <!-- Stats badges - clickable to filter -->
                <li>
                  <el-tag 
                    type="warning" 
                    class="stats-tag clickable-tag"
                    :class="{ 'tag-selected': selectedStatus === 'INITIALIZED' }"
                    @click="filterByStatus('INITIALIZED')"
                  >
                    Queued: {{ stats.INITIALIZED || 0 }}
                  </el-tag>
                </li>
                <li>
                  <el-tag 
                    type="info" 
                    class="stats-tag clickable-tag"
                    :class="{ 'tag-selected': selectedStatus === 'PROCESSING' }"
                    @click="filterByStatus('PROCESSING')"
                  >
                    Processing: {{ stats.PROCESSING || 0 }}
                  </el-tag>
                </li>
                <li>
                  <el-tag 
                    type="danger" 
                    class="stats-tag clickable-tag"
                    :class="{ 'tag-selected': selectedStatus === 'FAILED_BEFORE_SEND' }"
                    @click="filterByStatus('FAILED_BEFORE_SEND')"
                  >
                    Failed (Rate Limit): {{ stats.FAILED_BEFORE_SEND || 0 }}
                  </el-tag>
                </li>
                <li>
                  <el-tag 
                    class="stats-tag total-tag clickable-tag"
                    :class="{ 'tag-selected': selectedStatus === '' }"
                    @click="filterByStatus('')"
                  >
                    Total: {{ (stats.INITIALIZED || 0) + (stats.PROCESSING || 0) }}
                  </el-tag>
                </li>
              </ul>
            </el-col>
            <el-col :xl="10">
              <ul class="d-flex justify-content-end list-items-group-15">
                <li>
                  <div class="form-element search-input-lg">
                    <el-input
                      v-model="searchAccession"
                      placeholder="Search Accession ID"
                      clearable
                      @input="onSearchInput"
                      @clear="onSearchClear"
                      style="width: 200px"
                    />
                  </div>
                </li>
                <li>
                  <el-button
                    class="btn button-with-icon"
                    @click="refreshData"
                    :loading="loading"
                  >
                    <span class="mr-8">
                      <img
                        src="@/assets/img/icons/regenerate.svg"
                        alt="icon"
                        class="inactive"
                        style="width: 16px; height: 16px;"
                      />
                    </span>
                    <span>Refresh</span>
                  </el-button>
                </li>
              </ul>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>
    <div class="container-default" style="padding-top: 0">
      <div class="primary-content-view-new" v-loading="loading">
        <div class="listings listings__table listings__all-cases">
          <!-- Empty state -->
          <div v-if="!loading && !faxLogs.length" class="empty-state">
            <img
              src="@/assets/images/backgrounds/no-data-img.svg"
              alt="No data"
              style="width: 120px; opacity: 0.7;"
            />
            <p class="text-muted mt-3">No fax logs found</p>
          </div>

          <!-- Data table -->
          <el-table
            v-else
            :data="faxLogs"
            height="calc(100vh - 250px)"
            style="width: 100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: '600' }"
          >
            <el-table-column
              label="CREATED"
              min-width="160"
            >
              <template #default="scope">
                {{ formatDate(scope.row.created_at) }}
              </template>
            </el-table-column>

            <el-table-column
              label="ACCESSION ID"
              min-width="140"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.case_id?.accession_id"
                  class="accession-link"
                  @click="goToCaseView(scope.row.case_id._id)"
                >
                  {{ scope.row.case_id.accession_id }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>

            <el-table-column
              label="Fax ID"
              min-width="180"
            >
              <template #default="scope">
                <div v-if="scope.row.efax_id" class="fax-id-container">
                  <el-tag size="small" type="success" effect="plain" class="fax-type-tag">eFax</el-tag>
                  <span class="rc-id-text">{{ scope.row.efax_id }}</span>
                </div>
                <div v-else-if="scope.row.ringcentral_id" class="fax-id-container">
                  <el-tag size="small" type="info" effect="plain" class="fax-type-tag">RC</el-tag>
                  <span class="rc-id-text">{{ scope.row.ringcentral_id }}</span>
                </div>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>

            <el-table-column
              label="PATIENT"
              min-width="150"
            >
              <template #default="scope">
                <span v-if="scope.row.case_id?.patient">
                  {{ scope.row.case_id.patient.first_name }} {{ scope.row.case_id.patient.last_name }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>

            <el-table-column
              label="HOSPITAL"
              min-width="180"
            >
              <template #default="scope">
                <span v-if="scope.row.case_id?.hospital">
                  {{ scope.row.case_id.hospital.name }}
                </span>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>

            <el-table-column
              label="RECIPIENTS"
              min-width="180"
            >
              <template #default="scope">
                <div class="recipients-list">
                  <el-tag
                    v-for="(recipient, idx) in scope.row.recipients"
                    :key="idx"
                    size="small"
                    type="info"
                    class="recipient-tag"
                  >
                    {{ recipient }}
                  </el-tag>
                </div>
              </template>
            </el-table-column>

            <el-table-column
              label="SOURCE"
              min-width="100"
            >
              <template #default="scope">
                <el-tag 
                  :type="scope.row.source === 'resend_fax' ? '' : 'info'" 
                  size="small"
                  :class="scope.row.source === 'resend_fax' ? 'bulk-source-tag' : ''"
                >
                  {{ scope.row.source === 'resend_fax' ? 'BULK' : 'DIRECT' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              label="STATUS"
              min-width="140"
            >
              <template #default="scope">
                <el-tag 
                  :type="getStatusType(scope.row.status)" 
                  size="small"
                  :class="isResendStatus(scope.row.status) ? 'bulk-status-tag' : ''"
                >
                  {{ getStatusLabel(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              label="MESSAGE STATUS"
              min-width="140"
            >
              <template #default="scope">
                <span :class="getMessageStatusClass(scope.row.message_status)">
                  {{ scope.row.message_status || '-' }}
                </span>
              </template>
            </el-table-column>

            <el-table-column
              label="RETRY COUNT"
              min-width="110"
              align="center"
            >
              <template #default="scope">
                <el-tag
                  :type="scope.row.app_retry_count > 0 ? 'warning' : 'info'"
                  size="small"
                >
                  {{ scope.row.app_retry_count || 0 }} / 3
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              label="LAST UPDATED"
              min-width="160"
            >
              <template #default="scope">
                {{ formatDate(scope.row.webhook_updated_at || scope.row.updated_at) }}
              </template>
            </el-table-column>

            <el-table-column
              label="ERROR"
              min-width="200"
            >
              <template #default="scope">
                <el-tooltip
                  v-if="scope.row.error"
                  :content="scope.row.error"
                  placement="top"
                  :show-after="300"
                >
                  <span class="error-text">
                    {{ truncateError(scope.row.error) }}
                  </span>
                </el-tooltip>
                <span v-else class="text-muted">-</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="listings listings__pagination">
          <el-pagination
            v-model:currentPage="pagination.page"
            v-model:page-size="pagination.limit"
            :page-sizes="[25, 50, 75, 100]"
            layout="prev, pager, next, jumper, sizes, total"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "@/config/axios";
import moment from "moment";

export default {
  name: "FaxInProcessCases",
  data() {
    return {
      faxLogs: [],
      loading: false,
      searchAccession: "",
      searchTimeout: null,
      selectedStatus: "",
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
      },
      stats: {
        INITIALIZED: 0,
        PROCESSING: 0,
        IN_PROGRESS_RESEND: 0,
      },
      refreshInterval: null,
    };
  },
  mounted() {
    // Refresh bulk status from eFax API on page load (also fetches stats and logs)
    this.refreshData();
    
    // Auto-refresh every minute
    this.refreshInterval = setInterval(() => {
      this.refreshData();
    }, 60000);
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
        };

        if (this.searchAccession) {
          params.accession_id = this.searchAccession;
        }

        if (this.selectedStatus) {
          params.status = this.selectedStatus;
        }

        const response = await axios.get("/fax-logs/in-process", { params });

        if (response.data.success) {
          this.faxLogs = response.data.data;
          this.pagination = response.data.pagination;
          // Stats are now included in the same response
          if (response.data.stats) {
            this.stats = response.data.stats;
          }
        }
      } catch (error) {
        console.error("Error fetching fax logs:", error);
        this.$message.error("Failed to fetch fax logs");
      } finally {
        this.loading = false;
      }
    },

    onSearchInput() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchLogs();
      }, 500);
    },

    onSearchClear() {
      this.searchAccession = "";
      this.pagination.page = 1;
      this.fetchLogs();
    },

    filterByStatus(status) {
      this.selectedStatus = status;
      this.pagination.page = 1;
      this.fetchLogs();
    },

    async refreshData() {
      this.loading = true;
      try {
        // First, refresh bulk status from eFax API
        const bulkResponse = await axios.get("/fax-logs/refresh-bulk-status", {
          params: { hours: 720 }
        });
        
        if (bulkResponse.data.success) {
          const result = bulkResponse.data.data;
          
          // Show notification if any faxes were updated
          if (result.updated > 0) {
            let message = `Updated ${result.updated} fax(es): `;
            const parts = [];
            if (result.completed > 0) parts.push(`${result.completed} completed`);
            if (result.failed > 0) parts.push(`${result.failed} failed`);
            if (result.still_processing > 0) parts.push(`${result.still_processing} still processing`);
            message += parts.join(', ');
            
            this.$message.success(message);
            
            // Show additional notification if any faxes moved to "Fax Not Sent"
            if (result.moved_to_fax_not_sent > 0) {
              this.$notify({
                title: 'Faxes Moved',
                message: `${result.moved_to_fax_not_sent} failed fax(es) moved to "Fax Not Sent" screen`,
                type: 'warning',
                duration: 5000,
              });
            }
          } else {
            this.$message.info("No status changes detected");
          }
        }
      } catch (error) {
        console.error("Error refreshing bulk status:", error);
        this.$message.warning("Could not refresh from eFax API, showing cached data");
      }
      
      // Fetch updated logs (stats are now included in the same response)
      await this.fetchLogs();
      this.loading = false;
    },

    handleSizeChange(size) {
      this.pagination.limit = size;
      this.pagination.page = 1;
      this.fetchLogs();
    },

    handleCurrentChange(page) {
      this.pagination.page = page;
      this.fetchLogs();
    },

    goToCaseView(caseId) {
      this.$router.push({
        name: "CaseView",
        params: { case_id: caseId },
      });
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("MMM D, YYYY h:mm A");
    },

    truncateError(error) {
      if (!error) return "";
      return error.length > 50 ? error.substring(0, 50) + "..." : error;
    },

    getStatusType(status) {
      const types = {
        INITIALIZED: "warning",
        PROCESSING: "info",
        SUCCESS: "success",
        FAILED: "danger",
        IN_PROGRESS_RESEND: "info",
      };
      return types[status] || "info";
    },

    getStatusLabel(status) {
      const labels = {
        INITIALIZED: "Queued",
        PROCESSING: "Processing",
        SUCCESS: "Success",
        FAILED: "Failed",
        IN_PROGRESS_RESEND: "Bulk Sending",
      };
      return labels[status] || status;
    },

    isResendStatus(status) {
      return status === 'IN_PROGRESS_RESEND';
    },

    getMessageStatusClass(messageStatus) {
      if (!messageStatus) return "text-muted";
      
      const classes = {
        Queued: "status-queued",
        Received: "status-received",
        Sent: "status-sent",
        Delivered: "status-delivered",
        SendingFailed: "status-failed",
        DeliveryFailed: "status-failed",
      };
      return classes[messageStatus] || "text-muted";
    },
  },
};
</script>

<style lang="scss" scoped>
.fax-in-process-view {
  .navbarComponent {
    background: #fff;
    padding: 15px 0;
    border-bottom: 1px solid #ebeef5;
  }

  .container-default {
    padding: 20px;
  }

  .list-items-group-15 {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    li {
      display: flex;
      align-items: center;
    }
  }

  .stats-tag {
    font-size: 13px;
    font-weight: 500;
  }

  .clickable-tag {
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .tag-selected {
    box-shadow: 0 0 0 3px rgba(10, 93, 166, 0.3);
    transform: scale(1.05);
  }

  .bulk-source-tag {
    background-color: #9c27b0 !important;
    border-color: #9c27b0 !important;
    color: #fff !important;
  }

  .bulk-status-tag {
    background-color: #7b1fa2 !important;
    border-color: #7b1fa2 !important;
    color: #fff !important;
  }

  .total-tag {
    background-color: #0a5da6 !important;
    border-color: #0a5da6 !important;
    color: #fff !important;
  }

  .btn.button-with-icon {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 15px;
    font-size: 14px;
    font-weight: 500;
    background-color: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    color: #606266;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background-color: #f5f7fa;
      border-color: #c0c4cc;
    }

    .mr-8 {
      margin-right: 8px;
    }
  }

  .primary-content-view-new {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    padding: 0;
  }

  .listings__table {
    padding: 0;
  }

  .listings__pagination {
    padding: 15px 20px;
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: flex-end;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #909399;
  }

  .accession-link {
    color: #0a5da6;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  .recipients-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .recipient-tag {
    margin: 2px;
  }

  .error-text {
    color: #f56c6c;
    font-size: 12px;
    cursor: help;
  }

  .text-muted {
    color: #909399;
  }

  .status-queued {
    color: #e6a23c;
    font-weight: 500;
  }

  .status-received {
    color: #409eff;
    font-weight: 500;
  }

  .status-sent,
  .status-delivered {
    color: #67c23a;
    font-weight: 500;
  }

  .status-failed {
    color: #f56c6c;
    font-weight: 500;
  }

  .fax-id-container {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .fax-type-tag {
    font-size: 10px;
    padding: 0 4px;
    height: 18px;
    line-height: 16px;
  }

  .rc-id-text {
    font-family: 'Courier New', monospace;
    font-size: 11px;
    color: #606266;
    background: #f5f7fa;
    padding: 2px 6px;
    border-radius: 4px;
    word-break: break-all;
  }
}

// Element Plus table overrides
:deep(.el-table) {
  .el-table__header-wrapper {
    th {
      background-color: #f5f7fa !important;
      color: #606266;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
    }
  }

  .el-table__body-wrapper {
    .el-table__row {
      &:hover {
        td {
          background-color: #f5f7fa !important;
        }
      }
    }
  }

  .cell {
    font-size: 13px;
    line-height: 1.5;
  }
}

:deep(.el-pagination) {
  .el-pagination__total {
    margin-right: 10px;
  }
}
</style>

<style lang="scss">
// Global styles for notifications (not scoped)
.fax-success-notification {
  width: 450px !important;
  
  .el-notification__content {
    margin-top: 10px;
  }
}
</style>
